import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import {
    ResolutionFormError,
    apiErrorCode,
    resolutionErrorText,
} from './einvoice-resolution-error'
import type { SkipFormValues } from '@/lib/schemas/einvoice-resolution-schemas'
import type { EInvoiceResolutionSource } from '@/lib/api/einvoice-api'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Field, FieldError } from '@/components/ui/field'
import { Textarea } from '@/components/ui/textarea'
import { useSkipEInvoice } from '@/hooks/use-einvoice-resolution-mutations'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { m } from '@/lib/paraglide/messages'
import {
    SKIP_REASONS,
    SKIP_REASON_LABELS,
    createSkipSchema,
} from '@/lib/schemas/einvoice-resolution-schemas'

interface Props {
    orderId: string
    /** A draft saved at MISA is still waiting for someone to sign it. */
    hasOldDraft: boolean
    source: EInvoiceResolutionSource
    onClose: () => void
    /** The old MISA draft turned out to be signed already: close and look it up. */
    onAlreadyIssued?: () => void
}

/** "Không xuất": record why this order gets no invoice. */
export function EInvoiceSkipDialog({
    orderId,
    hasOldDraft,
    source,
    onClose,
    onAlreadyIssued,
}: Props) {
    const mutation = useSkipEInvoice()
    const workflow = useWorkflowTracking('einvoice_skip', {
        attemptKey: orderId,
        operation: 'submit',
    })
    const form = useForm<SkipFormValues>({
        resolver: zodResolver(createSkipSchema()),
        defaultValues: { note: '', delete_old_draft: hasOldDraft },
    })
    const [formError, setFormError] = useState<string>()
    const noteRequired = form.watch('reason') === 'other'

    const close = () => {
        workflow.finish('closed')
        onClose()
    }

    const submit = form.handleSubmit(
        async (values) => {
            setFormError(undefined)
            try {
                await workflow.run(
                    () =>
                        mutation.mutateAsync({
                            orderId,
                            input: {
                                ...values,
                                delete_old_draft:
                                    hasOldDraft && values.delete_old_draft,
                                source,
                            },
                        }),
                    (invoice) => invoice?.id,
                )
                onClose()
            } catch (error) {
                if (
                    apiErrorCode(error) === 'EINVOICE_ALREADY_ISSUED_ON_MISA' &&
                    onAlreadyIssued
                ) {
                    onClose()
                    onAlreadyIssued()
                    return
                }
                setFormError(resolutionErrorText(error))
            }
        },
        () => workflow.step('validation_failed'),
    )

    return (
        <Dialog open onOpenChange={(open) => !open && close()}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{m.einvoice_skip_title()}</DialogTitle>
                    <DialogDescription>
                        {m.einvoice_skip_desc()}
                    </DialogDescription>
                </DialogHeader>
                <FormProvider {...form}>
                    <form onSubmit={submit} noValidate className="space-y-4">
                        <div className="space-y-2">
                            {SKIP_REASONS.map((reason) => (
                                <label
                                    key={reason}
                                    className="flex items-center gap-2 rounded-md border p-2.5 text-sm"
                                >
                                    <input
                                        type="radio"
                                        value={reason}
                                        {...form.register('reason')}
                                    />
                                    {SKIP_REASON_LABELS[reason]()}
                                </label>
                            ))}
                            <FieldError name="reason" />
                        </div>
                        <Field
                            name="note"
                            label={m.einvoice_skip_note()}
                            required={noteRequired}
                        >
                            <Textarea
                                id="note"
                                rows={2}
                                {...form.register('note')}
                            />
                        </Field>
                        {hasOldDraft ? (
                            <label className="flex items-start gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    className="mt-0.5"
                                    {...form.register('delete_old_draft')}
                                />
                                {m.einvoice_delete_old_draft()}
                            </label>
                        ) : null}
                        <ResolutionFormError message={formError} />
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={close}
                                disabled={mutation.isPending}
                            >
                                {m.einvoice_dialog_cancel()}
                            </Button>
                            <Button type="submit" disabled={mutation.isPending}>
                                {m.einvoice_skip_save()}
                            </Button>
                        </DialogFooter>
                    </form>
                </FormProvider>
            </DialogContent>
        </Dialog>
    )
}
